import { ArrowBackOutlined } from '@mui/icons-material'
import { Box, Icon, LinearProgress, Typography } from '@mui/material'
import { memo, ReactElement, useEffect, useMemo, useState } from 'react'
import {
    Navigate,
    useLocation,
    useNavigate,
    useSearchParams,
} from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../redux/hook'
import {
    ISettingsResponse,
    getApplicationSettings,
} from '../../services/applications'
import { IApplicationSettingsType } from '../Applications/interfaces'
import {
    Applications,
    publicShipmentView,
    RoutePaths,
    URLSearchParamsKeys,
    UserPermissions,
} from '../../constants/constants'
import ErrorBoundary from '../ErrorBoundaries/ErrorBoundary'
import { getApplicationSettingsForGuest } from '../../services/guestServices'
import DetailsView from './DetailsView'
import TabsView from './TabsView'
import {
    getEntityPreviewColumnsOrder,
    getEntityRelations,
    getEntityViewDetails,
    resetEntityView,
} from '../../redux/slices/entityViewSlice'
import { useTranslation } from 'react-i18next'
import GuestUserUnitPreferences from '../Applications/GuestApps/GuestUserUnitPreferences'
import { isEmptyObject } from '../../utils/StringUtils'

const ViewEntityInstance = (): ReactElement => {
    const { t } = useTranslation()
    const loggedInOrgId = useAppSelector((state) => state.org.org?.id)
    const user = useAppSelector((state) => state.user.user)
    const permissions = useAppSelector((state) => state.org.permissions)
    const error = useAppSelector((state) => state.entityInstanceView.error)
    const entityData = useAppSelector(
        (state) => state.entityInstanceView.entityData,
    )
    const isLoadingEntityData = useAppSelector(
        (state) => state.entityInstanceView.isLoadingEntityData,
    )
    const isLoadingEntityRelations = useAppSelector(
        (state) => state.entityInstanceView.isLoadingEntityRelations,
    )
    const entityTemplateRelations = useAppSelector(
        (state) => state.entityInstanceView.entityTemplateRelations,
    )
    const { templateName } = entityData
    const [searchParams] = useSearchParams()
    const isGuestUser =
        useAppSelector((state) => state.searchParams.isGuestUser) === 'true'
    const publicSearchTemplateId = useAppSelector(
        (state) => state.searchParams.templateId,
    )
    const publicSearchOrgId = useAppSelector(
        (state) => state.searchParams.orgId,
    )
    const publicSearchViewId = useAppSelector(
        (state) => state.searchParams.instanceId,
    )

    const isPublicShipmentPreView =
        useAppSelector((state) => state.searchParams.publicShipmentView) ===
        publicShipmentView.SHIPMENT_PREVIEW

    const orgId = isPublicShipmentPreView
        ? publicSearchOrgId || ''
        : loggedInOrgId || ''
    const entityTemplateId =
        (isPublicShipmentPreView
            ? publicSearchTemplateId
            : searchParams.get(URLSearchParamsKeys.templateId)) || ''
    const viewID =
        (isPublicShipmentPreView
            ? publicSearchViewId
            : searchParams.get(URLSearchParamsKeys.instanceId)) || ''

    const isAdmin = useMemo(
        () =>
            permissions.includes(UserPermissions.ORG_ADMIN) ||
            permissions.includes(UserPermissions.USER_ADMIN),
        [permissions],
    )

    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const onBack = () => {
        navigate(-1)
    }

    //graph expand
    const [graphExpand, setGraphExpand] = useState(false)

    const [applicationSettings, setApplicationSettings] =
        useState<IApplicationSettingsType>({} as IApplicationSettingsType)
    const [isLoadingSettings, setIsLoadingSettings] = useState(true)

    const location = useLocation()
    const queryParams = new URLSearchParams(location.search)
    const applicationId = isPublicShipmentPreView
        ? Applications.inTransit.id
        : queryParams.get('applicationId') || ''

    const routePathname = decodeURIComponent(location.pathname)
    const guestUserPreferredUnit = useAppSelector(
        (state) => state.entityInstanceView.guestUserPreferredUnit,
    )

    useEffect(() => {
        if (orgId && viewID && entityTemplateId) {
            dispatch(
                getEntityViewDetails({
                    orgId,
                    recordId: viewID,
                    entityTemplateId,
                    isGuestUser,
                    guestUserPreferredUnit,
                }),
            )
        }
    }, [
        orgId,
        viewID,
        entityTemplateId,
        user,
        isGuestUser,
        dispatch,
        guestUserPreferredUnit,
    ])

    useEffect(() => {
        if (orgId && entityTemplateId) {
            dispatch(
                getEntityRelations({
                    orgId,
                    entityTemplateId,
                    isGuestUser,
                }),
            )
            //columns order
            dispatch(
                getEntityPreviewColumnsOrder({
                    entityTemplateId,
                    orgId,
                    isGuestUser,
                    isAdmin,
                }),
            )
        }
    }, [orgId, entityTemplateId, isGuestUser, isAdmin, dispatch])

    useEffect(() => {
        /**
         * TODO: This useEffect can be entirely removed. Implement below code changes.
         * 1) Store the Facility Monitoring Settings in its redux slice similar to that of intransit settings.
         * 2) Then we can get these settings on the case basis and send the Graph.tsx component.
         * 3) Remove this useEffect altogether
         */
        const getAppSettings = async () => {
            setIsLoadingSettings(true)
            const appSettingsResponse: ISettingsResponse<IApplicationSettingsType> =
                isGuestUser
                    ? await getApplicationSettingsForGuest<IApplicationSettingsType>(
                          orgId,
                          applicationId,
                      )
                    : await getApplicationSettings<IApplicationSettingsType>(
                          orgId || '',
                          applicationId,
                      )
            const settings = appSettingsResponse?.appSettings
            setApplicationSettings(settings)
            setIsLoadingSettings(false)
        }
        if (orgId) {
            if (applicationId) {
                getAppSettings()
            } else {
                setIsLoadingSettings(false)
            }
        }
    }, [applicationId, orgId, isGuestUser])

    useEffect(() => {
        return () => {
            dispatch(resetEntityView())
        }
    }, [dispatch])

    if (
        isLoadingSettings ||
        (isEmptyObject(entityData) && isLoadingEntityData) ||
        (isLoadingEntityRelations && entityTemplateRelations.length === 0)
    ) {
        return <LinearProgress />
    }

    if (error) {
        /**
         * Develop a error page component later and display it instead of this navigating away to home route.
         */
        if (isGuestUser) {
            return <Navigate to="/guestUser/shipment" /> //this home URL for gust user
        } else {
            return <Navigate to="/" />
        }
    }

    return (
        <Box
            sx={{
                padding:
                    routePathname === RoutePaths.entityPreview
                        ? '15px 10px 10px 10px'
                        : '0px',
                height: '100%',
            }}
        >
            {(isLoadingEntityData || isLoadingEntityRelations) && (
                <LinearProgress
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '2px',
                        zIndex: 1000,
                    }}
                />
            )}
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ marginBottom: '15px' }}
            >
                <Box sx={{ display: 'flex' }}>
                    <Icon
                        id="view-entity-back-button"
                        onClick={onBack}
                        sx={{ marginRight: '10px', cursor: 'pointer' }}
                        fontSize="small"
                    >
                        <ArrowBackOutlined />
                    </Icon>
                    <Typography variant="h4" component="h2">
                        {t(templateName)}
                    </Typography>
                </Box>
                {isGuestUser && (
                    <Box>
                        <GuestUserUnitPreferences />
                    </Box>
                )}
            </Box>
            <Box
                display="flex"
                alignItems="flex-start"
                sx={{
                    height: 'calc(100% - 55px)',
                    overflow: 'auto',
                }}
            >
                {!graphExpand && (
                    <Box
                        sx={{
                            width: 'calc(25% - 10px)',
                            marginRight: '10px',
                        }}
                    >
                        <DetailsView
                            key={viewID}
                            applicationSettings={applicationSettings}
                        />
                    </Box>
                )}
                <Box
                    sx={{
                        flexGrow: '1',
                        width: graphExpand ? 'calc(100%)' : 'calc(65%)',
                    }}
                >
                    <TabsView
                        key={viewID}
                        graphExpand={graphExpand}
                        setGraphExpand={setGraphExpand}
                        applicationSettings={applicationSettings}
                    />
                </Box>
            </Box>
        </Box>
    )
}

const ViewEntityInstanceWrapper = memo(() => {
    return (
        <ErrorBoundary>
            <ViewEntityInstance />
        </ErrorBoundary>
    )
})

ViewEntityInstanceWrapper.displayName = 'ViewEntityInstanceWrapper'

export default ViewEntityInstanceWrapper
